import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/Components/ui/dropdown-menu";
import StatusGlyph from "@/Components/Passport/StatusGlyph";
import { normalizeStatus, PUBLISH_STATUS_LABELS, targetsFrom } from "@/lib/publishStatus";
import { ChevronDown, LockIcon } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { toast } from "sonner";
import ArchiveDialog from "./ArchiveDialog";
import ReleaseDialog from "./ReleaseDialog";
import useGrants from "@/Hooks/useGrants";

/** How often the cascade badge asks the run how far it has got. */
const POLL_INTERVAL_MS = 2000;

/** Run statuses that mean "still working"; everything else is terminal. */
const RUNNING_STATUSES = ['pending', 'running'];

/**
 * How many polls in a row may fail before the badge gives up on the run.
 *
 * A single failure is nothing — the next poll answers. A run of them is not:
 * the endpoint is scoped to the caller's current team, so switching team in
 * another tab turns every later poll into a permanent 404, and without a cap
 * the interval would outlive the page, asking every two seconds behind a badge
 * frozen at counts that stopped being true.
 */
const POLL_FAILURE_LIMIT = 5;

/**
 * How long a live channel may say nothing about a run in flight before the
 * badge polls again anyway. A working cascade pushes about once a second; a
 * silence this long means the pushes are not getting through (the realtime
 * worker down, the server's broadcasts misconfigured) while the socket stays
 * up, or the run is still queued, and either way polling is what the badge
 * did before it was live. The next push stops it again.
 */
const LIVE_QUIET_MS = 15_000;

/**
 * The badge beside the status pill while a run moves a passport's subtree:
 * "Releasing… 312/768" for a release's cascade, "Archiving… 312/768" for an
 * archive's, with a spinner, from the run's processed and total counts.
 * Nothing without a run. Presentational: PublishStatusControl follows the
 * run (pushed live, or polled) and hands it in.
 */
export function ReleaseRunBadge({ run }) {
    if (!run) {
        return null;
    }
    const runLabel = `${run.mode === 'archive' ? 'Archiving…' : 'Releasing…'} ${run.processed_count ?? 0}/${run.total_count ?? 0}`;

    return (
        <span role="status" className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md pl-2 pr-2.5 text-[12.5px] font-medium tabular-nums text-zinc-700 ring-1 ring-inset ring-zinc-200">
            <svg className="size-3 animate-spin text-blue-500" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <circle cx="8" cy="8" r="6" stroke="currentColor" strokeOpacity=".25" strokeWidth="2" />
                <path d="M14 8a6 6 0 0 0-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            {runLabel}
        </span>
    );
}

/**
 * The status pill and the menu of moves the lifecycle allows from where the
 * passport is: draft → released | archived, released → revising | archived,
 * revising → released | archived. Archived is final, so its pill opens
 * nothing.
 *
 * The moves are read from `lib/publishStatus` rather than written out here,
 * so the browser's list cannot drift from App\Enums\PublishStatus. Each says
 * beside it what it would do — a new release number or a return to the old
 * one, values unlocked while the public keeps the release, an archive that is
 * final and takes the subtree.
 *
 *   - **Released** opens {@see ReleaseDialog} instead of writing. A release is
 *     billable, locks the passport's values, and may take a whole subtree with
 *     it — none of which a click on a menu item should commit to silently.
 *   - **Archived** opens {@see ArchiveDialog}: it cannot be undone and always
 *     takes everything beneath, which the dialog counts before it asks.
 *   - **Revising** writes straight away; it touches nothing beneath and the
 *     public keeps seeing the release until the next one.
 *
 * When a release or an archive opens a cascade, the run is bookkeeping the
 * page has to follow until it reaches a terminal status, and then the
 * passport is reloaded, since its descendants have just changed. While the
 * passport's channel is `live`, the page hands over each run the server
 * pushes (`liveRun`, from `release_run.updated`) and the badge asks nothing:
 * a run in flight is shown, whoever started it, and the one on show closes
 * when its own terminal status arrives. While it isn't live (no socket, a
 * drop), the badge polls as it always has, and so it does while a live
 * channel stays silent about a run in flight (LIVE_QUIET_MS). A channel that
 * comes up, or back, missed whatever was pushed before, so the badge reads
 * the run once then.
 */
export default function PublishStatusControl({ passportData, ontologyId = null, refetchTrigger, live = false, liveRun = null }) {
    const [releaseOpen, setReleaseOpen] = useState(false);
    const [archiveOpen, setArchiveOpen] = useState(false);
    const consequenceId = useId();
    // A cascade already running when the page loads — started before a
    // reload, or in another tab — is handed over with the passport, so the
    // badge picks up where it was instead of vanishing until the run ends.
    const [run, setRun] = useState(passportData?.active_release_run ?? null);
    const handedRunId = passportData?.active_release_run?.id ?? null;
    useEffect(() => {
        if (handedRunId !== null) {
            setRun((current) => current ?? passportData.active_release_run);
        }
    }, [handedRunId]);
    const { reasonFor } = useGrants();

    // The prop is re-created by the parent on every render; the poll must not
    // restart every time that happens.
    const refetch = useRef(refetchTrigger);
    refetch.current = refetchTrigger;

    const runId = run?.id ?? null;
    const runStatus = run?.status ?? null;
    // What the run is doing to the subtree, for its toasts; a run keeps its
    // mode for life, so it needs no restart of the poll.
    const runWork = run?.mode === 'archive' ? 'archive' : 'release';

    // The run reached a terminal status. The subtree moved underneath the
    // page, so what it is showing is out of date.
    const finish = (data, work) => {
        setRun(null);
        refetch.current?.();

        if ((data?.failed_count ?? 0) > 0 || data?.status === 'failed') {
            toast.error(`The ${work} finished with failures; check the passports beneath.`);
        }
    };

    // The run on show, for the live handler below, which must not restart on
    // every count it applies; and how many live runs have been applied, so a
    // read sent before one of them cannot put older counts back.
    const shownRun = useRef(run);
    shownRun.current = run;
    const liveApplied = useRef(0);

    // A live channel silent about the run on show for LIVE_QUIET_MS; the
    // badge then polls as if it were not live, until the next push.
    const [quiet, setQuiet] = useState(false);
    const following = live && !quiet;

    useEffect(() => {
        if (!live || runId === null || !RUNNING_STATUSES.includes(runStatus)) {
            setQuiet(false);

            return undefined;
        }

        const timer = setTimeout(() => setQuiet(true), LIVE_QUIET_MS);

        return () => clearTimeout(timer);
    }, [live, runId, runStatus, liveRun]);

    useEffect(() => {
        if (!liveRun || liveRun.id === undefined || liveRun.id === null) {
            return;
        }

        liveApplied.current += 1;
        setQuiet(false);

        if (RUNNING_STATUSES.includes(liveRun.status)) {
            setRun(liveRun);

            return;
        }

        // A terminal status closes only the run it is about.
        if (shownRun.current?.id === liveRun.id) {
            finish(liveRun, liveRun.mode === 'archive' ? 'archive' : 'release');
        }
    }, [liveRun]);

    // Whether the channel was live when this last looked, so that coming up
    // (or back) is told apart from staying up.
    const wasLive = useRef(live);

    useEffect(() => {
        const cameUp = live && !wasLive.current;
        wasLive.current = live;

        if (runId === null || !RUNNING_STATUSES.includes(runStatus)) {
            return undefined;
        }

        if (following) {
            if (!cameUp) {
                return undefined;
            }

            // One read for what was pushed before the channel was up. A
            // failure is let be: the run's next push answers it, or the
            // poll a quiet channel falls back to.
            let stale = false;
            const seen = liveApplied.current;

            axios.get(route('releases.show', { run: runId }))
                .then(({ data }) => {
                    if (stale || seen !== liveApplied.current) {
                        return;
                    }

                    if (RUNNING_STATUSES.includes(data?.status)) {
                        setRun(data);

                        return;
                    }

                    finish(data, runWork);
                })
                .catch(() => {});

            return () => {
                stale = true;
            };
        }

        let cancelled = false;
        let failures = 0;

        const timer = setInterval(() => {
            axios.get(route('releases.show', { run: runId }))
                .then(({ data }) => {
                    if (cancelled) {
                        return;
                    }

                    failures = 0;

                    if (RUNNING_STATUSES.includes(data?.status)) {
                        setRun(data);

                        return;
                    }

                    finish(data, runWork);
                })
                // A poll that fails is a poll; the next one answers. Losing the
                // badge over one bad response would be worse than the gap — but
                // a poll that has stopped answering altogether is not coming
                // back, and the interval has to end with the badge.
                .catch(() => {
                    if (cancelled) {
                        return;
                    }

                    failures += 1;

                    if (failures < POLL_FAILURE_LIMIT) {
                        return;
                    }

                    clearInterval(timer);
                    setRun(null);
                    toast.error(`Lost track of the ${runWork} run; refresh to see its result.`);
                });
        }, POLL_INTERVAL_MS);

        return () => {
            cancelled = true;
            clearInterval(timer);
        };
    }, [runId, runStatus, live, following]);

    if (!passportData) {
        return null;
    }

    const status = normalizeStatus(passportData.publish_status);
    const statusLabel = PUBLISH_STATUS_LABELS[status] ?? status;
    const releaseNumber = Number(passportData.release_number ?? 0);
    const options = targetsFrom(status).map((value) => ({ value, label: PUBLISH_STATUS_LABELS[value] ?? value }));

    // What choosing an option would do, said beside it rather than in a
    // paragraph above the list, and read out as the item's description; the
    // accessible name stays the bare status the item moves to.
    const consequence = (value) => {
        switch (value) {
            case 'released':
                // From revising with nothing written since, the release is
                // the one the public already has.
                return status === 'revising' && !passportData.unreleased_changes
                    ? `returns to #${releaseNumber}`
                    : `creates #${releaseNumber + 1}`;
            case 'revising':
                return `unlocks values; the public keeps seeing release #${releaseNumber} until you release again`;
            case 'archived':
                return 'final; takes everything under it';
            default:
                return null;
        }
    };

    // A draft never reached the public, so archiving it is an ordinary
    // passports:write. Every other move releases, or leaves a release, and
    // is a releases:write.
    const abilityFor = (value) => (status === 'draft' && value === 'archived' ? 'passports:write' : 'releases:write');

    const permissionReason = (value) => reasonFor(abilityFor(value), ontologyId);

    // Said once in the menu body, so the reason is read rather than hovered for.
    const permissionNotes = [...new Set(options.map(({ value }) => permissionReason(value)).filter(Boolean))];

    const applyResult = (data) => {
        if (data?.release_run) {
            setRun(data.release_run);
        }

        refetch.current?.();
    };

    const revise = () => {
        const request = axios.post(route('passport.publish_status.update', { snowflake: passportData.snowflake }), {
            publish_status: 'revising',
        }).then((response) => {
            applyResult(response.data);

            return response.data;
        });

        toast.promise(request, {
            loading: 'Saving...',
            success: 'Revising — values unlocked',
            // A refusal names the rule it broke; a generic failure does not.
            error: (error) => error?.response?.data?.message || 'Failed to update publish status',
        });

        // toast.promise consumes the rejection, but the branch above is a
        // derived promise of its own and would otherwise go unhandled.
        request.catch(() => {});
    };

    const choose = (value) => {
        if (value === 'released') {
            setReleaseOpen(true);
        } else if (value === 'archived') {
            setArchiveOpen(true);
        } else if (value === 'revising') {
            revise();
        }
    };

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild disabled={options.length === 0}>
                    <button
                        type="button"
                        aria-label="Publish status"
                        title={options.length === 0 ? 'Archived passports are final' : undefined}
                        className="inline-flex h-8 items-center gap-2 whitespace-nowrap rounded-md bg-white pl-2.5 pr-2 text-[13px] font-medium text-zinc-800 ring-1 ring-inset ring-zinc-200 transition-colors hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/15 disabled:pr-2.5 disabled:hover:bg-white"
                    >
                        <StatusGlyph status={status} decorative />
                        {statusLabel}
                        {status === 'released' && releaseNumber > 0 && (
                            <span className="font-normal tabular-nums text-zinc-400">#{releaseNumber}</span>
                        )}
                        {options.length > 0 && <ChevronDown className="size-3.5 text-zinc-400" aria-hidden="true" />}
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80 p-1">
                    <p className="px-2 pb-1 pt-1.5 text-[11.5px] font-medium text-zinc-500">Set status</p>
                    {permissionNotes.map((note) => (
                        <p key={note} className="flex items-start gap-1.5 px-2 py-1.5 text-[12px] text-zinc-500">
                            <LockIcon className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                            {note}
                        </p>
                    ))}
                    {options.map(({ value, label }) => (
                        <DropdownMenuPrimitive.Item
                            key={value}
                            onSelect={() => choose(value)}
                            disabled={permissionReason(value) !== null}
                            title={permissionReason(value) ?? undefined}
                            aria-describedby={`${consequenceId}-${value}`}
                            className="relative flex cursor-default select-none items-start gap-2.5 rounded-md px-2 py-1.5 text-[13px] text-zinc-800 outline-none transition-colors focus:bg-zinc-100 data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                        >
                            <StatusGlyph status={value} decorative className="mt-[5px]" />
                            <span className="min-w-0">
                                {label}
                                <span
                                    id={`${consequenceId}-${value}`}
                                    aria-hidden="true"
                                    className="block text-[11.5px] leading-4 tabular-nums text-zinc-500"
                                >
                                    {consequence(value)}
                                </span>
                            </span>
                        </DropdownMenuPrimitive.Item>
                    ))}
                    <DropdownMenuSeparator className="bg-zinc-100" />
                    <p className="flex items-start gap-2 px-2 py-1.5 text-[11.5px] leading-4 text-zinc-500">
                        <LockIcon className="mt-px size-3 shrink-0" aria-hidden="true" />
                        Releasing locks every value. To change values later, move the passport to Revising — the public keeps the released version until you release again.
                    </p>
                </DropdownMenuContent>
            </DropdownMenu>

            <ReleaseRunBadge run={run} />

            <ReleaseDialog
                open={releaseOpen}
                onOpenChange={setReleaseOpen}
                snowflake={passportData.snowflake}
                ontologyId={ontologyId}
                releaseNumber={releaseNumber}
                everReleased={(passportData.releases ?? []).length > 0}
                onReleased={applyResult}
            />

            <ArchiveDialog
                open={archiveOpen}
                onClose={() => setArchiveOpen(false)}
                snowflake={passportData.snowflake}
                label={passportData.friendly_id || passportData.snowflake}
                everReleased={(passportData.releases ?? []).length > 0}
                unreleasedChanges={status === 'revising' && Boolean(passportData.unreleased_changes)}
                ontologyId={ontologyId}
                ability={abilityFor('archived')}
                onDone={applyResult}
            />
        </>
    );
}
